import { IDataFieldsDrawing, IDataFieldsServiceAgreement, IDataFieldsServiceReport } from "@propely/propely-typings";
import { TooltipWrapper } from "@src/components/deprecated/DeprecatedTooltip/TooltipWrapper";
import { IconNames } from "@src/components/deprecated/Icon/IconNames";
import { TagsSelectPopup } from "@src/components/deprecated/TagsSelectPopup/TagsSelectPopup";
import { IBaseDocumentDataFields } from "@src/pages/FilesPage/FilesPageList/ExpandedFileElement/ExpandedFileElement.types";
import { Tag } from "data-hooks/company/tags/tags.type";
import { formatTagName } from "data-hooks/company/tags/tags.utils";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { FileLabel } from "../../../../../components/deprecated/FilePopup/FileLabel";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { ReadAccessPopup } from "../../../../../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { DataTableRelativesCell } from "../../../../../components/deprecated/RelativesEditorPopup/DataTableRelativesCell";
import { RelativesEditorPopup } from "../../../../../components/deprecated/RelativesEditorPopup/RelativesEditorPopup";
import { RenameFilePopup } from "../../../../../components/deprecated/RenameFilePopup/RenameFilePopup";
import { StatusTag, StatusTagColor } from "../../../../../components/deprecated/StatusTag/StatusTag";
import { FileTypeImageView } from "../../../../../components/ui-components/FileTypeImageView/FileTypeImageView";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { FileUtilities } from "../../../../../utils/fileUtilities";
import { Utilities } from "../../../../../utils/utilities";
import { GlobalFileData } from "../../../Files/GlobalFileData";
import { Role } from "../../../User/Role";
import { DataObjectTagField } from "../../DataObjectField/CustomFields/DeviationDataFields";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";
import { getExternalLink } from "./FileDocumentDataHelperUtilities";

export class GlobalFileDataHelper extends DataObjectHelper<GlobalFileData> {
  id = () => "globalFileDataHelper";

  displayName = () => t("main.file");

  displayNamePlural = () => t("main.files");

  name = new FirebaseDataStringField<GlobalFileData>("name", t("main.name"), (file) => file.name, IconNames.NAME);
  tag = new DataObjectTagField<GlobalFileData>("tags", t("main.tags"), (file) => file.tags, "category");
  size = new FirebaseDataNumberField<GlobalFileData>("size", t("main.fileSize_abb"), (file) => file.size);
  uploadDate = new FirebaseDataDateField<GlobalFileData>("createdBy.date", t("main.uploadDate"), (file) => file.createdBy?.date);
  createdByUser = new FirebaseDataStringField<GlobalFileData>("createdBy.user.name", t("main.uploadedBy"), (file) => file.createdBy?.user?.name);
  archived = new FirebaseDataBooleanField<GlobalFileData>("archived", t("main.archived"), (file) => file.archived);

  allFields(): DataObjectField<any, GlobalFileData>[] {
    return [this.name, this.tag, this.size, this.uploadDate, this.createdByUser, this.archived];
  }

  getAllDataTableDatas(documents: GlobalFileData[]): DataTableData[] {
    return documents.map((file, index) => {
      return {
        key: file.id,
        cells: {
          type: { element: <FileTypeImageView height="32px" fileDocument={file} />, rawString: file.type },
          tags: {
            element: (
              <>
                {file.tags.map((tag) => (
                  <TooltipWrapper
                    key={tag.id}
                    trigger={<StatusTag color={tag.badgeColor as StatusTagColor} text={formatTagName(tag)} />}
                    title={tag.description}
                  />
                ))}
              </>
            ),
            rawString: file.tags.map((tag) => formatTagName(tag)).join(", "),
          },
          relatives: { element: <DataTableRelativesCell relatives={file.relatives} />, rawString: file.relatives.map((r) => r.name).join(", ") },
          name: { element: <FileLabel dataCy={`file-label-${index}`} file={file} />, rawString: file.name, rawData: file },
          size: FileUtilities.getFileSizeString(file.size),
          visibility: Utilities.getReadAccessString(file.readAccess),
          uploadDate: file.createdBy?.date,
          createdByUser: file.createdBy?.user?.name,
          archived: file.archived ? t("main.yes") : t("main.no"),
        },
      };
    });
  }

  getServcieReportPredictedFields(): (keyof IDataFieldsServiceReport)[] {
    return ["contractorID", "responsiblePerson", "executedDate", "deviations"];
  }

  getAgreementPredictedFields(): (keyof IDataFieldsServiceAgreement)[] {
    return ["name", "contractorID", "agreementStartDate", "agreementEndDate", "agreementSignDate", "annualReportCount"];
  }

  getDrawingPredictedFields(): (keyof IDataFieldsDrawing)[] {
    return ["drawingTitle", "contractorID", "drawingNumber", "drawingDate", "discipline", "drawingCategory"];
  }

  getOtherPredictedFields(): (keyof IBaseDocumentDataFields)[] {
    return ["contractorID"];
  }

  /**
   *
   * @param dataFieldKey
   * @returns mapping of dataFieldKey to collection item field id
   */
  getServiceAgreementFieldIdFromDataField(dataFieldKey: keyof IDataFieldsServiceAgreement): string | undefined {
    switch (dataFieldKey) {
      case "contractorID":
        return "VvstlCB9Yw6AKc12I28Y"; // Contractors
      case "agreementStartDate":
        return "1hxGqd1NcekLeAkoXjlJ"; // Start date
      case "agreementEndDate":
        return "ZpFe8N8neIno6xa5aw6K"; // End date
      case "agreementSignDate":
        return undefined;
      default:
        return undefined;
    }
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "type",
        title: "",
        width: "30px",
      },
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "tags",
        title: t("main.tags"),
      },
      {
        key: "size",
        title: t("main.fileSize_abb"),
      },
      { key: "uploadDate", title: t("main.uploadDate") },
      { key: "createdByUser", title: t("main.uploadedBy") },
      {
        key: "relatives",
        title: t("main.connections"),
      },
      { key: "visibility", title: t("main.visibleFor") },
      { key: "archived", title: t("main.archived") },
    ];
  }

  override getActionsForData = (file: GlobalFileData): DataObjectAction<GlobalFileData>[] => {
    const actions: DataObjectAction<GlobalFileData>[] = [];
    const properties = LocalData.currentUser.properties.filter((p) => file.propertyIDs.includes(p.id));
    actions.push({
      id: "download",
      title: t("main.download"),
      iconName: "file_download",
      performAction: () => {
        file.downloadFile();
        return Promise.resolve();
      },
    });

    const link = getExternalLink(file);
    if (link) {
      actions.push({
        id: "openExternal",
        title: t("main.openExternalFileTitle", { name: link.name }),
        iconName: "open_in_new",
        maxSelected: 1,
        link: link.url,
      });
    }

    if (file.hasWriteAccess()) {
      actions.push({
        id: "changeReadAccess",
        iconName: "visibility",
        title: t("main.changeVisibility"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <ReadAccessPopup
                id={id}
                readAccess={file.readAccess}
                requiredAccess={{ roles: [Role.LANDLORD] }}
                onSelect={(access) => {
                  resolve({ dataObjects, data: access });
                }}
                accessGroups={["companies", "users", "contractorGroups"]}
                context={{ propertyID: undefined }}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(t("main.savingData"), file.saveUpdate({ readAccess: Utilities.convertReadAccess(updateData) }));
        },
      });

      actions.push({
        id: "tag",
        iconName: "label",
        title: t("main.addTags"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <TagsSelectPopup
                id={id}
                value={file.tags.map((tag) => tag.id)}
                onChange={(tags) => {
                  resolve({ dataObjects, data: tags });
                }}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(
            t("main.savingData"),
            file.saveUpdate({
              tags: (updateData as Tag[]).map((c) => c.id),
            })
          );
        },
      });

      actions.push({
        id: "addRelatives",
        iconName: "link",
        title: t("documentAI.connections.editConnections"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <RelativesEditorPopup
                id={id}
                allowSelectingProperty
                allowSelectingGlobalCollectionItems
                allowSelectingForeignCollectionItems
                property={file.propertyIDs.length > 0 ? properties[0] : undefined}
                relatives={file.relatives}
                didSelectRelatives={(relatives) => resolve({ dataObjects, data: relatives })}
                connectFrom="file"
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentSavingToast(
            file.saveUpdate({
              relatives: updateData,
            })
          );
        },
      });

      actions.push({
        id: "rename",
        iconName: "edit",
        title: t("main.editFileName"),
        maxSelected: 1,
        requiredConsent: (dataObjects) => {
          return Promise.resolve({ dataObjects, data: dataObjects });
        },
        performAction: (updateData) => {
          const file = updateData[0] as GlobalFileData;
          return Popup.present((id) => (
            <RenameFilePopup
              id={id}
              defaultFileName={file.name}
              fileType={file.type}
              handleSave={(name) => ToastUtilities.presentSavingToast(file.rename(name))}
            />
          )) as never;
        },
      });

      if (!file.archived) {
        actions.push({
          id: "archive",
          iconName: "archive",
          title: t("main.archive"),
          performAction: () => {
            return ToastUtilities.presentSavingToast(file.archive());
          },
        });
      } else {
        actions.push({
          id: "unarchive",
          iconName: "unarchive",
          title: t("main.unarchive"),
          performAction: () => {
            return ToastUtilities.presentSavingToast(file.archive());
          },
        });
      }

      actions.push({
        id: "delete",
        iconName: "delete",
        iconColor: "var(--main-red-700)",
        title: t("main.delete") + " " + t("main.file").toLowerCase(),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.name),
              () => {
                resolve({ dataObjects });
              },
              t("main.cannotBeUndone")
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentDeletingToast(file.deleteDocument());
        },
      });
    }

    return actions;
  };
}
